'use client';

import { useState } from 'react';
import { Settings2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { SkillSettingDTO } from '../store';
import { SkillSettingsDialog } from './SkillSettingsDialog';

interface SkillSettingsFormProps {
  skillId: string;
  skillName: string;
  settings: SkillSettingDTO[];
  onUpdate: (key: string, value: number) => void;
}

export function SkillSettingsForm({ skillId: _skillId, skillName, settings, onUpdate }: SkillSettingsFormProps) {
  const t = useTranslations('skills');
  const [dialogSetting, setDialogSetting] = useState<SkillSettingDTO | null>(null);

  if (settings.length === 0) return null;

  return (
    <div className="space-y-3">
      {settings.map((setting) => (
        <div key={setting.key} className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm font-medium capitalize">
              {setting.key.replace(/_/g, ' ')}
            </span>
            {setting.description && (
              <span className="text-xs text-muted-foreground">{setting.description}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <input
              type="number"
              value={setting.value}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) onUpdate(setting.key, v);
              }}
              className={cn(
                'w-16 rounded-md border border-border bg-surface px-2 py-1',
                'text-sm font-medium text-center',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                'transition-colors duration-150',
              )}
              min={0}
            />
            <button
              type="button"
              onClick={() => setDialogSetting(setting)}
              title={t('skillSettingsForm.askAiTooltip')}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md',
                'border border-border text-muted-foreground',
                'hover:bg-accent/10 hover:text-accent',
                'transition-colors duration-150',
              )}
            >
              <Settings2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      {dialogSetting && (
        <SkillSettingsDialog
          skillName={skillName}
          setting={dialogSetting}
          onConfirm={(value) => {
            onUpdate(dialogSetting.key, value);
            setDialogSetting(null);
          }}
          onClose={() => setDialogSetting(null)}
        />
      )}
    </div>
  );
}
